// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleConnectionPropertyDef } from '@article/constants/article-connection-property-def';
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { PropertyUtils } from '@article/util/property-utils';
import { ProjectModel } from '@project/model/project.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Log } from '@shared/debug/log';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionAreaUsageVO, ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { SuitableConnectionAreasVO } from '../model/vo/suitable-connection-areas.vo';
import { ArticleCreationManager } from '../manager/article-creation.manager';
import { CodingArticleProcessor } from './coding-article.processor';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

@Injectable({ providedIn: 'root' })
export class AddCodingArticleProcessor {
  private articleCreationManager: ArticleCreationManager = null;
  private articleManager: ArticleManager;
  private projectModel: ProjectModel;
  private codingArticleUtils: CodingArticleUtils;
  private alertHelper: AlertHelper;
  private connectionInfoService: ConnectionInfoService
  private codingArticleProcessor: CodingArticleProcessor;

  private _isInitialized = false;

    public initialize(
      articleManager: ArticleManager,
      projectModel: ProjectModel,
      codingArticleUtils: CodingArticleUtils,
      alertHelper: AlertHelper,
      connectionInfoService: ConnectionInfoService,
      codingArticleProcessor: CodingArticleProcessor
    ) {
      this.articleManager = articleManager;
      this.projectModel = projectModel;
      this.codingArticleUtils = codingArticleUtils;
      this.alertHelper = alertHelper;
      this.connectionInfoService = connectionInfoService;
      this.codingArticleProcessor = codingArticleProcessor;

      this.articleCreationManager = codingArticleProcessor.articleCreationManager

      this._isInitialized = true;
    }

    private checkInitialized() {
      if (!this._isInitialized) {
        throw new Error("AddCodingArticleProcessor is not initialized");
      }
    }

    public async addCodingFingerIfNecessary(detachedUsageId: string, alreadyDetachedUsageIds: string[] = null): Promise<void> {
      this.checkInitialized();
      const detachedUsage: ArticleUsageVO = this.projectModel.getArticleUsage(detachedUsageId);
      const detachedArticle: ArticleVO = detachedUsage ? this.articleManager.getCachedArticle(detachedUsage.articleId, detachedUsage.articleVersion) : null;
      if (detachedArticle) {
        if (CodingArticleUtils.isCodingBolt(detachedArticle, detachedUsage)) {
          await this.addCodingFingerByCodingBolt(detachedUsage, alreadyDetachedUsageIds);
        }
      }
    }

    private async addCodingFingerByCodingBolt(codingBoltUsage: ArticleUsageVO, alreadyDetachedUsageIds: string[] = null): Promise<void> {

      const firstConnectionUsage = ArticleUsageUtils.tryGetFirstAsConnectionAreaUsage(codingBoltUsage);
      if(!firstConnectionUsage) {
        return
      }
      const secondConnectionUsage = ArticleUsageUtils.tryGetSecondAsConnectionAreaUsage(codingBoltUsage);

      if (firstConnectionUsage.connection && firstConnectionUsage.connection.acceptingAreaId) {
        const bridgeArticleUsageId: string = firstConnectionUsage.connection.acceptingAreaId.articleUsageId;
        if (bridgeArticleUsageId && alreadyDetachedUsageIds && alreadyDetachedUsageIds.indexOf(bridgeArticleUsageId) > -1) {
          return;
        }
        const bridgeArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(bridgeArticleUsageId);
        if (bridgeArticleUsage) {
          const bridgeArticleVO = this.articleManager.getCachedArticle(bridgeArticleUsage.articleId, bridgeArticleUsage.articleVersion);
          let position: number = CodingArticleUtils.getPositionFromConnectionName(firstConnectionUsage.connection.acceptingAreaId.metaElementName);
          const connectionLeadingToCodingFinger: ConnectionAreaUsageVO =
            this.codingArticleUtils.searchCodingReferencesForConnectionLeadingToCodingFinger(
              bridgeArticleUsage,
              bridgeArticleVO,
              firstConnectionUsage.connection.acceptingAreaId.metaElementName,
              !secondConnectionUsage ? null : secondConnectionUsage.connection.acceptingAreaId.metaElementName
            );
          if (connectionLeadingToCodingFinger) {
            if (secondConnectionUsage) {
              position = CodingArticleUtils.getPositionFromConnectionName(connectionLeadingToCodingFinger.identity.metaElementName);
            }
            let connectedArticleUsageId: string = null;
            if (
              ArrayUtils.isNotEmpty(connectionLeadingToCodingFinger.connection.givingAreaIds) &&
              (connectionLeadingToCodingFinger.connection.givingAreaIds[0] as MetaElementUsageIdentityVO)
            ) {
              connectedArticleUsageId = connectionLeadingToCodingFinger.connection.givingAreaIds[0].articleUsageId;
            }
            if (connectedArticleUsageId) {
              if (connectedArticleUsageId === bridgeArticleUsage.id) {
                // in this case the connection is a giving connection and we should check the id of the accepting connection
                connectedArticleUsageId = connectionLeadingToCodingFinger.connection.acceptingAreaId.articleUsageId;
              }
              const connectedArticleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(connectedArticleUsageId);
              if (connectedArticleUsage) {
                const connectedArticle: ArticleVO = this.articleManager.getCachedArticle(
                  connectedArticleUsage.articleId,
                  connectedArticleUsage.articleVersion
                );
                if (CodingArticleUtils.hasCodingFingerConnection(connectedArticle)) {
                  if (alreadyDetachedUsageIds && alreadyDetachedUsageIds.indexOf(connectedArticleUsage.id) > -1) {
                    return;
                  }
                  await this.addCodingFinger(connectedArticleUsage, position);
                } else {
                  const conBackwardUsages: ArticleUsageVO[] = [];
                  const cids: string[] = ArticleUsageUtils.getBackwardConnectedArticleUsageIds(connectedArticleUsage);
                  for (const bcid of cids) {
                    const usage: ArticleUsageVO = this.projectModel.getArticleUsage(bcid);
                    if (usage) {
                      conBackwardUsages.push(usage);
                    }
                  }
                  for (const conBackwardUsage of conBackwardUsages) {
                    const conBackwardArticle: ArticleVO = this.articleManager.getCachedArticle(conBackwardUsage.articleId, conBackwardUsage.articleVersion);
                    if (CodingArticleUtils.hasCodingFingerConnection(conBackwardArticle)) {
                      if (alreadyDetachedUsageIds && alreadyDetachedUsageIds.indexOf(conBackwardUsage.id) > -1) {
                        return;
                      }
                      await this.addCodingFinger(conBackwardUsage, position);
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    public async addCodingFinger(articleUsage: ArticleUsageVO, position: number = 1, metaElementName: string = '', disableAutomaticClean: boolean = false): Promise<void> {
      this.checkInitialized();
      if (!articleUsage) {
        return;
      }
      const codingFingerArticleId: number = parseInt(CodingArticleUtils.getRemovedCodingFingerArticleId(articleUsage, metaElementName), 10);
      const codingFingerArticleVersion: number = parseInt(CodingArticleUtils.getRemovedCodingFingerArticleVersion(articleUsage, metaElementName), 10);
      let codingFingerArticle: ArticleVO = null;
      if (isNaN(codingFingerArticleId) || codingFingerArticleId === 0) {
        return;
      }
      if (this.articleManager.isArticleCached(codingFingerArticleId, codingFingerArticleVersion)) {
        codingFingerArticle = this.articleManager.getCachedArticle(codingFingerArticleId, codingFingerArticleVersion);
        await this.continueAddingCodingFinger(articleUsage, codingFingerArticle, position, metaElementName, disableAutomaticClean);
      } else {
        try {
          const article: ArticleVO = await this.articleManager.loadFullArticle(codingFingerArticleId, codingFingerArticleVersion).toPromise();
          if (article) {
            this.loadCodingFingerArticleSuccess(article, articleUsage, position, metaElementName, disableAutomaticClean);
          } else {
            this.loadCodingFingerArticleSuccessFailure('');
          }
        } catch (error) {
          Log.error('Could not load full article: ', error);
          this.loadCodingFingerArticleSuccessFailure(error);
        }
      }
    }

    private async loadCodingFingerArticleSuccess(article: ArticleVO, receivingArticleUsage: ArticleUsageVO, position: number,
      metaElementName: string, disableAutomaticClean: boolean): Promise<void> {
      if (receivingArticleUsage && article) {
        await this.continueAddingCodingFinger(receivingArticleUsage, article, position, metaElementName, disableAutomaticClean);
      }
    }

    private async continueAddingCodingFinger(articleUsage: ArticleUsageVO, codingFingerArticle: ArticleVO, position: number,
      metaElementName: string, disableAutomaticClean: boolean): Promise<void> {
      const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
      const suitableGivingAreas: SuitableConnectionAreasVO = new SuitableConnectionAreasVO();
      suitableGivingAreas.article = codingFingerArticle;
      suitableGivingAreas.connectionAreas = [];
      let i: number = 0;
      while (i <= codingFingerArticle.metaElements.length) {
        const connectionAreaVO: ConnectionAreaVO = codingFingerArticle.metaElements[i] as ConnectionAreaVO;
        if (
          connectionAreaVO &&
          connectionAreaVO.connectionSide === ConnectionAreaVO.SIDE_GIVING &&
          (PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionFeatures, ArticleConnectionPropertyDef.TYPE) ===
            ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE ||
            PropertyUtils.getStringPropertyValue(connectionAreaVO.connectionFeatures, ArticleConnectionPropertyDef.TYPE) ===
            ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE)
        ) {
          suitableGivingAreas.connectionAreas.push(connectionAreaVO);
        }
        i++;
      }
      const connections: Connection[] = [];
      if (!CodingArticleUtils.hasMultipleCodingFingerConnections(article)) {
        position = 1;
      }
      let codingFingerConnectionName: string = metaElementName;
      if (!codingFingerConnectionName || codingFingerConnectionName.length < 1) {
        codingFingerConnectionName = CodingArticleUtils.findCodingFingerConnectionName(article, position);
      }
      const metaElementUsageIdentityVO: MetaElementUsageIdentityVO = new MetaElementUsageIdentityVO();
      metaElementUsageIdentityVO.articleUsageId = articleUsage.id.toString();
      metaElementUsageIdentityVO.metaElementName = codingFingerConnectionName;
      const connection: Connection = this.connectionInfoService.findConnectionByIdentity(metaElementUsageIdentityVO);
      if (connection) {
        connections.push(connection);
      }
      if (disableAutomaticClean === false) {
        this.codingArticleProcessor.enableCodingFingerAttachedHandler = true;
      }
      await this.articleCreationManager.createArticlesBySuitableGivingConnectionAreasAndAcceptingCubes(suitableGivingAreas, connections, true);

    }

    private loadCodingFingerArticleSuccessFailure(message: string): void {
      this.alertHelper.showError('global.title.error', message);
    }
}
